import { Formik, Field, Form as FormikForm, ErrorMessage, type FormikHelpers } from "formik"
import * as Yup from "yup"
import {
  PageContainer,
  VisualSection,
  FormSection,
  Title,
  InputGroup,
  Label,
  Input,
  Error,
  SubmitButton,
  Logo,
  GridRow,
  TextArea,
  Logo2,
  DropdownContainer,
  FormEyebrow,
  FormIntro,
  TrustList,
  DirectContact,
  ChoiceGroup,
  SectionDivider,
} from "./Style"
import SvgLogo from "../../../app/assets/img/logo.svg"
import { useEffect, useState } from "react"
import missionImage from "@assets/img/mission-image.jpg"
import api from "@utils/api.client"
import { trackEvent } from "@components/utils/tracking.client"

import { DESIRED_SIZE_OPTIONS } from "./constants/desiredSizeOptions"
import { FormikMultiSelectDropdown } from "@components/filters/FilterDropdown/FormikMultiSelectDropdown"
import FormikCustomSelect from "@components/base/Dropdown/FormikCustomSelect"

// Initial form values
const initialValues = {
  // 1. Address: if applicant leaves empty and checks the box, they want to be proposed an apartment
  apartmentAddress: "",
  proposeApartment: false,

  // 2. Date to move in
  moveInDate: "",
  // 3. Desired size
  desiredSize: [] as string[],
  // 4. Number of occupants
  numberOfOccupants: 1,
  // 5. Quebec residency duration: "plus d'un an" or "moins d'un an"
  quebecResidenceDuration: "",
  // 6. Comfortable with credit check
  comfortableWithCreditCheck: "",
  // 7. Has guarantor
  hasGuarantor: "",
  // 8. Animals: comma-separated list (will be split into an array)
  animals: "",
  // 9. Visit availability (multiple choices)
  visitAvailability: [] as string[],
  // 10. Applicant contact information
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  // 11. Additional specifications
  additionalSpecifications: "",

  status: "Demande reçue",
  submittedAt: new Date().toISOString(),
}

const validationSchema = Yup.object({
  apartmentAddress: Yup.string().test(
    "apartmentAddress-required",
    "Veuillez indiquer l'adresse ou choisir l'option de proposition.",
    function (value) {
      const { proposeApartment } = this.parent
      if (!proposeApartment && (!value || value.trim() === "")) {
        return false
      }
      return true
    },
  ),
  proposeApartment: Yup.boolean(),
  moveInDate: Yup.string().required("La date d'emménagement est requise."),
  desiredSize: Yup.array()
    .of(Yup.string().oneOf(DESIRED_SIZE_OPTIONS, "Option invalide"))
    .min(1, "Veuillez sélectionner au moins une grandeur désirée.")
    .required("La grandeur désirée est requise."),
  numberOfOccupants: Yup.number().min(1, "Au moins un occupant est requis.").required("Le nombre d'occupants est requis."),
  quebecResidenceDuration: Yup.string()
    .oneOf(["plus d'un an", "moins d'un an"], "Choisissez une option valide.")
    .required("Cette information est requise."),
  comfortableWithCreditCheck: Yup.string().oneOf(["true", "false"], "Cette information est requise.").required("Cette information est requise."),
  hasGuarantor: Yup.string().oneOf(["true", "false"], "Cette information est requise.").required("Cette information est requise."),
  animals: Yup.string(),
  visitAvailability: Yup.array()
    .of(Yup.string().oneOf(["Soir de semaine", "fin de semaine en am", "fin de semaine en pm"]))
    .min(1, "Sélectionnez au moins une option pour la disponibilité de visite.")
    .required("La disponibilité pour une visite est requise."),
  firstName: Yup.string().required("Le prénom est requis."),
  lastName: Yup.string().required("Le nom est requis."),
  email: Yup.string().email("Adresse courriel invalide.").required("Le courriel est requis."),
  phone: Yup.string()
    .matches(/^\D*(\d\D*){10}$/, "Numéro de téléphone invalide (10 chiffres requis).")
    .required("Le téléphone est requis."),
  additionalSpecifications: Yup.string(),
  status: Yup.string(),
  submittedAt: Yup.string(),
})

const RequestApplicationForm = () => {
  const [successMessage, setSuccessMessage] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [addresses, setAddresses] = useState<PropertyAddress[]>([])
  const [submitError, setSubmitError] = useState("")

  interface PropertyAddress {
    id: string
    address: string
  }

  useEffect(() => {
    async function fetchAddresses() {
      try {
        const response = await api.get<Array<{ _id: string; address: string }>>("/properties-to-rent/currently-rentable")
        // Map backend property objects to our simplified type
        const props: PropertyAddress[] = response.data.map((prop) => ({
          id: prop._id,
          address: prop.address,
        }))
        setAddresses(props)
      } catch {
        setAddresses([])
      }
    }
    fetchAddresses()
  }, [])

  const handleSubmit = async (values: typeof initialValues, { resetForm }: FormikHelpers<typeof initialValues>) => {
    if (isSubmitting) return
    setIsSubmitting(true)
    setSubmitError("")

    // Process the animals field: split comma-separated values into array (trim spaces)
    const processedValues = {
      ...values,
      comfortableWithCreditCheck: values.comfortableWithCreditCheck === "true",
      hasGuarantor: values.hasGuarantor === "true",
      submittedAt: new Date().toISOString(),
      animals: values.animals
        ? values.animals
            .split(",")
            .map((a) => a.trim())
            .filter(Boolean)
        : [],
      phone: values.phone.replace(/\D/g, ""),
      numberOfOccupants: Number(values.numberOfOccupants),
    }

    try {
      const response = await api.post("/applications-for-rent", processedValues)
      // console.log("Form submitted successfully:", response)

      if (response.status === 200 || response.status === 201) {
        trackEvent("rental_application_submit", { form_name: "demande_location" })
        setSuccessMessage(true)
        resetForm()
      }
    } catch {
      setSubmitError("La demande n’a pas pu être envoyée. Réessayez ou communiquez avec notre équipe.")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (successMessage) {
    return (
      <PageContainer>
        <VisualSection src={missionImage} alt="" />
        <Logo href="/" aria-label="Retour à l’accueil">
          <SvgLogo />
        </Logo>
        <FormSection>
          <FormEyebrow>Demande reçue</FormEyebrow>
          <Title>Merci. Votre recherche nous a été transmise.</Title>
          <FormIntro>Nous communiquerons avec vous si un logement correspond aux critères fournis ou si une précision est nécessaire.</FormIntro>
          <DirectContact>
            <a href="/">Retour au site</a> · <a href="mailto:info@tbgestionimmobiliere.com">Écrire à l’équipe</a>
          </DirectContact>
        </FormSection>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <VisualSection src={missionImage} alt="" />
      <Logo href="/" aria-label="Retour à l’accueil">
        <SvgLogo />
      </Logo>

      <FormSection>
        <FormEyebrow>Candidature locative</FormEyebrow>
        <Title>Présentez votre recherche de logement.</Title>
        <FormIntro>
          Le formulaire nous aide à vérifier les logements disponibles et à organiser les prochaines étapes. Une demande ne garantit pas l’attribution
          d’un logement.
        </FormIntro>
        <TrustList>
          <li>Critères présentés clairement</li>
          <li>Consentement avant toute vérification</li>
          <li>Renseignements confidentiels</li>
        </TrustList>
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ values }) => {
            return (
              <FormikForm>
                {/* 1. Address or propose option */}
                <InputGroup>
                  <Label htmlFor="apartmentAddress">Quelle est l'adresse de l'appartement qui vous intéresse ?</Label>
                  <FormikCustomSelect
                    name="apartmentAddress"
                    options={[{ value: "", label: "Sélectionnez une adresse" }, ...addresses.map((a) => ({ value: a.address, label: a.address }))]}
                    placeholder="Sélectionnez une adresse..."
                    disabled={values.proposeApartment}
                  />
                  <ErrorMessage name="apartmentAddress" component={Error} />
                  <ChoiceGroup>
                    <label>
                      <Field type="checkbox" name="proposeApartment" />
                      Me proposer un appartement correspondant à mes critères
                    </label>
                  </ChoiceGroup>
                </InputGroup>

                {/* 2. Date to move in */}
                <InputGroup>
                  <Label htmlFor="moveInDate">Pour quelle date souhaitez-vous emménager ?</Label>
                  <Field as={Input} id="moveInDate" name="moveInDate" type="date" />
                  <ErrorMessage name="moveInDate" component={Error} />
                </InputGroup>

                {/* 3. Desired size */}
                <InputGroup>
                  <Label htmlFor="desiredSize">Grandeur désirée ?</Label>
                  <DropdownContainer>
                    <FormikMultiSelectDropdown
                      name="desiredSize"
                      options={DESIRED_SIZE_OPTIONS.map((option) => ({ value: option, label: option }))}
                      // onChange={(newVal) => setFieldValue("desiredSize", newVal)}
                      placeholder="Sélectionnez une ou plusieurs grandeurs..."
                    />
                  </DropdownContainer>
                  <ErrorMessage name="desiredSize" component={Error} />
                </InputGroup>

                {/* 4. Number of occupants */}
                <InputGroup>
                  <Label htmlFor="numberOfOccupants">Nombre total d’occupants</Label>
                  <Field as={Input} id="numberOfOccupants" name="numberOfOccupants" type="number" />
                  <ErrorMessage name="numberOfOccupants" component={Error} />
                </InputGroup>

                {/* 5. Quebec residency duration */}
                <InputGroup>
                  <Label>Vous êtes au Québec depuis ?</Label>
                  <ChoiceGroup role="group" aria-labelledby="quebecResidenceDuration">
                    <label>
                      <Field type="radio" name="quebecResidenceDuration" value="plus d'un an" />
                      Plus d'un an
                    </label>
                    <label>
                      <Field type="radio" name="quebecResidenceDuration" value="moins d'un an" />
                      Moins d'un an
                    </label>
                  </ChoiceGroup>
                  <ErrorMessage name="quebecResidenceDuration" component={Error} />
                </InputGroup>

                {/* 6. Credit check */}
                <InputGroup>
                  <Label>Nous effectuerons une enquête de crédit. Êtes-vous d'accord avec cela?</Label>
                  <ChoiceGroup role="group" aria-labelledby="comfortableWithCreditCheck">
                    <label>
                      <Field type="radio" name="comfortableWithCreditCheck" value="true" />
                      Oui
                    </label>
                    <label>
                      <Field type="radio" name="comfortableWithCreditCheck" value="false" />
                      Non
                    </label>
                  </ChoiceGroup>
                  <ErrorMessage name="comfortableWithCreditCheck" component={Error} />
                </InputGroup>

                {/* 7. Guarantor */}
                <InputGroup>
                  <Label>Avez-vous accès à une personne pouvant se porter garant (endosseur) ?</Label>
                  <ChoiceGroup role="group" aria-labelledby="hasGuarantor">
                    <label>
                      <Field type="radio" name="hasGuarantor" value="true" />
                      Oui
                    </label>
                    <label>
                      <Field type="radio" name="hasGuarantor" value="false" />
                      Non
                    </label>
                  </ChoiceGroup>
                  <ErrorMessage name="hasGuarantor" component={Error} />
                </InputGroup>

                {/* 8. Animals */}
                <InputGroup>
                  <Label htmlFor="animals">Avez-vous des animaux ? (Spécifiez, ex: chien, chat)</Label>
                  <Field as={Input} id="animals" name="animals" placeholder="Séparez par une virgule" />
                  <ErrorMessage name="animals" component={Error} />
                </InputGroup>

                {/* 9. Visit availability */}
                <InputGroup>
                  <Label>À quel moment êtes-vous disponible pour une visite?</Label>
                  <ChoiceGroup role="group" aria-labelledby="visitAvailability">
                    <label>
                      <Field type="checkbox" name="visitAvailability" value="Soir de semaine" />
                      Soir de semaine
                    </label>
                    <label>
                      <Field type="checkbox" name="visitAvailability" value="fin de semaine en am" />
                      Fin de semaine en matinée
                    </label>
                    <label>
                      <Field type="checkbox" name="visitAvailability" value="fin de semaine en pm" />
                      Fin de semaine en après-midi
                    </label>
                  </ChoiceGroup>
                  <ErrorMessage name="visitAvailability" component={Error} />
                </InputGroup>

                <SectionDivider>
                  <strong>Vos coordonnées</strong>
                  <span>Utilisées pour répondre à cette demande et organiser une visite, s’il y a lieu.</span>
                </SectionDivider>
                <GridRow columns="3fr 3fr">
                  <InputGroup>
                    <Label htmlFor="firstName">Prénom</Label>
                    <Field as={Input} id="firstName" name="firstName" />
                    <ErrorMessage name="firstName" component={Error} />
                  </InputGroup>
                  <InputGroup>
                    <Label htmlFor="lastName">Nom</Label>
                    <Field as={Input} id="lastName" name="lastName" />
                    <ErrorMessage name="lastName" component={Error} />
                  </InputGroup>
                </GridRow>
                <GridRow columns="5fr 3fr">
                  <InputGroup>
                    <Label htmlFor="email">Courriel</Label>
                    <Field as={Input} id="email" name="email" type="email" autoComplete="email" />
                    <ErrorMessage name="email" component={Error} />
                  </InputGroup>
                  <InputGroup>
                    <Label htmlFor="phone">Téléphone</Label>
                    <Field as={Input} id="phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" />
                    <ErrorMessage name="phone" component={Error} />
                  </InputGroup>
                </GridRow>

                {/* 11. Additional specifications */}
                <InputGroup>
                  <Label htmlFor="additionalSpecifications">Autres précisions (facultatif)</Label>
                  <Field
                    as={TextArea}
                    id="additionalSpecifications"
                    name="additionalSpecifications"
                    placeholder="Besoins particuliers, date flexible ou renseignement utile…"
                  />
                </InputGroup>

                {/* Hidden fields for status and submittedAt */}
                <Field type="hidden" name="status" />
                <Field type="hidden" name="submittedAt" />

                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Envoi en cours…" : "Envoyer la demande"}
                </SubmitButton>

                {submitError && <Error role="alert">{submitError}</Error>}
                <DirectContact>
                  En envoyant le formulaire, vous autorisez TB Gestion Immobilière à communiquer avec vous au sujet de cette demande. Consultez notre{" "}
                  <a href="/politique-de-confidentialite">politique de confidentialité</a>.
                </DirectContact>

                <Logo2 href="/">
                  <SvgLogo />
                </Logo2>
              </FormikForm>
            )
          }}
        </Formik>
      </FormSection>
    </PageContainer>
  )
}

export default RequestApplicationForm
